import React, { useState } from 'react';
import {
  Button, Stack, Avatar, Typography, Menu, MenuItem, ListItemIcon,
  ListItemText, Divider, Box, alpha, useTheme, useMediaQuery
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { ScaleAnimation } from '../../animations/MotionWrapper';
import { useAuth } from '../../../context/AuthContext';
import NotificationBell from '../../notifications/NotificationBell';
import { ROUTES } from '../../../constants/routes';
import ThemeToggle from './ThemeToggle';

/**
 * UserMenu - User profile dropdown menu for authenticated users
 * Displays user avatar, name, and menu with profile/logout options
 */
const UserMenu = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = async () => {
    handleCloseMenu();
    await logout();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  if (!currentUser) return null;

  const firstLetter = currentUser.username ? currentUser.username[0].toUpperCase() : '?';

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ ml: 'auto' }}
    >
      {/* Notification bell */}
      <Box sx={{ width: 40, display: 'flex', justifyContent: 'center' }}>
        {currentUser && <NotificationBell />}
      </Box>

      <ScaleAnimation>
        <Button
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleOpenMenu}
          sx={{
            color: theme.palette.common.white,
            textTransform: 'none',
            borderRadius: 2,
            whiteSpace: 'nowrap',
            minWidth: { xs: 40, sm: 120 },
            '&:hover': {
              backgroundColor: alpha(theme.palette.common.white, 0.1),
            },
          }}
          endIcon={!isMobile && <ExpandMoreIcon />}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: theme.palette.secondary.main,
              fontSize: '0.9rem',
              mr: isMobile ? 0 : 1,
              boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.15)}`,
              border: `2px solid ${alpha(theme.palette.common.white, 0.8)}`
            }}
          >
            {firstLetter}
          </Avatar>
          {!isMobile && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {currentUser.username}
            </Typography>
          )}
        </Button>
      </ScaleAnimation>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'user-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            overflow: 'visible',
            minWidth: 200,
            borderRadius: 2,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
      >
        {/* User info header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle2" color="text.primary" fontWeight="bold">
            {currentUser.username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {currentUser.role}
          </Typography>
        </Box>

        {/* Profile link */}
        <MenuItem component={Link} to={ROUTES.PROFILE} onClick={handleCloseMenu} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="Profil" />
        </MenuItem>

        {/* Theme toggle */}
        <ThemeToggle variant="menuitem" onClose={handleCloseMenu} />

        <Divider sx={{ my: 0.5 }} />

        {/* Logout */}
        <MenuItem onClick={handleLogoutClick} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText primary="Abmelden" />
        </MenuItem>
      </Menu>
    </Stack>
  );
};

export default UserMenu;
